import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/apiClient';
import apiClient from '../utils/apiClient';
import {
  Calendar, ChevronLeft, ChevronRight, Plus, X, Clock,
  User, Phone, Mail, FileText, Loader2, Check, Trash2, ArrowLeft
} from 'lucide-react';

const HORAS = ['08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00'];
const TIPOS = ['consulta', 'audiencia', 'conciliacion'];
const ESTADOS = ['pendiente', 'confirmada', 'completada', 'cancelada'];
const DIAS = ['Lun','Mar','Mié','Jue','Vie'];

const estadoColor = {
  pendiente: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  confirmada: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  completada: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  cancelada: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const tipoColor = {
  consulta: 'border-l-blue-500',
  audiencia: 'border-l-violet-500',
  conciliacion: 'border-l-emerald-500',
};

function getWeekDays(anchor) {
  const d = new Date(anchor);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  const days = [];
  for (let i = 0; i < 5; i++) {
    const dd = new Date(monday);
    dd.setDate(monday.getDate() + i);
    days.push(dd);
  }
  return days;
}

function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

const Calendario = () => {
  const navigate = useNavigate();
  const [citas, setCitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchor, setAnchor] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [selectedCita, setSelectedCita] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    cliente_nombre: '', cliente_telefono: '', cliente_email: '',
    cliente_documento: '', expediente: '', tipo: 'consulta',
    fecha: '', hora: '09:00', notas: '', estado: 'pendiente',
  });

  const weekDays = useMemo(() => getWeekDays(anchor), [anchor]);

  const fetchCitas = useCallback(async () => {
    setLoading(true);
    try {
      const from = toDateStr(weekDays[0]);
      const to = toDateStr(weekDays[4]);
      const data = await api.get(`/citas?fecha_desde=${from}&fecha_hasta=${to}`);
      // The endpoint might not support range yet, get the month
      const mes = `${weekDays[2].getFullYear()}-${String(weekDays[2].getMonth()+1).padStart(2,'0')}`;
      const allData = await api.get(`/citas?mes=${mes}`);
      setCitas(Array.isArray(allData) ? allData : (allData.data || []));
    } catch { setCitas([]); }
    finally { setLoading(false); }
  }, [weekDays]);

  useEffect(() => { fetchCitas(); }, [fetchCitas]);

  const goToday = () => setAnchor(new Date());

  const openNew = (fecha, hora) => {
    setSelectedCita(null);
    setForm({
      cliente_nombre: '', cliente_telefono: '', cliente_email: '',
      cliente_documento: '', expediente: '', tipo: 'consulta',
      fecha: fecha || toDateStr(new Date()), hora: hora || '09:00',
      notas: '', estado: 'pendiente',
    });
    setShowModal(true);
  };

  const openEdit = (cita) => {
    setSelectedCita(cita);
    setForm({ ...cita });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.cliente_nombre || !form.fecha || !form.hora) return;
    setSaving(true);
    try {
      if (selectedCita) {
        await apiClient.put(`/citas/${selectedCita.id}`, form);
      } else {
        await apiClient.post('/citas', form);
      }
      setShowModal(false);
      fetchCitas();
    } catch { alert('Error al guardar la cita'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta cita?')) return;
    try {
      await apiClient.delete(`/citas/${id}`);
      fetchCitas();
    } catch { alert('Error al eliminar'); }
  };

  const getCitasForSlot = (dateStr, hora) =>
    citas.filter(c => c.fecha === dateStr && c.hora === hora);

  const todayStr = toDateStr(new Date());

  const inputClass = "w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none";
  const labelClass = "block text-xs font-semibold text-slate-500 dark:text-gray-400 mb-1";

  return (
    <div className="h-full overflow-hidden bg-slate-100 dark:bg-gray-950 flex flex-col">

      {/* Header */}
      <div className="px-4 md:px-6 pt-4 pb-3 bg-white/90 dark:bg-gray-900/90 border-b border-gray-200 dark:border-gray-800 backdrop-blur-xl flex-shrink-0">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Calendar size={20} className="text-blue-500" /> Calendario de Citas
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {weekDays[0].toLocaleDateString('es-CO', {day:'numeric',month:'short'})} — {weekDays[4].toLocaleDateString('es-CO', {day:'numeric',month:'short', year:'numeric'})}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/encuestas')} className="p-2 rounded-lg bg-slate-200 dark:bg-gray-700 hover:bg-slate-300 dark:hover:bg-gray-600 text-slate-600 dark:text-gray-300"><ArrowLeft size={16} /></button>
            <button onClick={() => setAnchor(prev => { const d = new Date(prev); d.setDate(d.getDate() - 7); return d; })} className="p-2 rounded-lg bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700"><ChevronLeft size={16} /></button>
            <button onClick={goToday} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700">Hoy</button>
            <button onClick={() => setAnchor(prev => { const d = new Date(prev); d.setDate(d.getDate() + 7); return d; })} className="p-2 rounded-lg bg-slate-100 dark:bg-gray-800 hover:bg-slate-200 dark:hover:bg-gray-700"><ChevronRight size={16} /></button>
            <button onClick={() => openNew()} className="ml-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 hover:bg-blue-700 shadow-sm">
              <Plus size={14} /> Nueva Cita
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 overflow-auto p-2 md:p-4">
        {loading ? (
          <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-blue-500" size={40} /></div>
        ) : (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-slate-200 dark:border-gray-800 overflow-hidden">
            {/* Day headers */}
            <div className="grid grid-cols-[60px_repeat(5,1fr)] border-b border-slate-200 dark:border-gray-800">
              <div className="p-2" />
              {weekDays.map((d, i) => {
                const isToday = toDateStr(d) === todayStr;
                return (
                  <div key={i} className={`p-2 text-center border-l border-slate-100 dark:border-gray-800 ${isToday ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}>
                    <p className="text-[10px] font-bold text-slate-400 uppercase">{DIAS[i]}</p>
                    <p className={`text-lg font-bold ${isToday ? 'text-blue-600' : 'text-slate-700 dark:text-white'}`}>{d.getDate()}</p>
                  </div>
                );
              })}
            </div>

            {/* Time slots */}
            {HORAS.map(hora => (
              <div key={hora} className="grid grid-cols-[60px_repeat(5,1fr)] border-b border-slate-50 dark:border-gray-800/50 min-h-[60px]">
                <div className="p-2 text-[10px] font-bold text-slate-400 text-right pr-3 pt-1">{hora}</div>
                {weekDays.map((d, i) => {
                  const dateStr = toDateStr(d);
                  const slotCitas = getCitasForSlot(dateStr, hora);
                  const isToday = dateStr === todayStr;
                  return (
                    <div key={i}
                      onClick={() => openNew(dateStr, hora)}
                      className={`border-l border-slate-100 dark:border-gray-800 p-0.5 cursor-pointer hover:bg-blue-50/50 dark:hover:bg-blue-900/5 transition-colors ${isToday ? 'bg-blue-50/30 dark:bg-blue-900/5' : ''}`}>
                      {slotCitas.map(c => (
                        <div key={c.id}
                          onClick={(e) => { e.stopPropagation(); openEdit(c); }}
                          className={`text-[10px] px-1.5 py-1 rounded-md mb-0.5 border-l-2 cursor-pointer hover:shadow-sm transition-all ${tipoColor[c.tipo] || 'border-l-gray-400'} ${estadoColor[c.estado] || ''}`}>
                          <p className="font-bold truncate">{c.cliente_nombre}</p>
                          <p className="opacity-70 truncate">{c.tipo}</p>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 w-full max-w-lg rounded-2xl shadow-2xl border border-slate-200 dark:border-gray-800 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 dark:border-gray-800 flex justify-between items-center">
              <h3 className="font-bold text-slate-800 dark:text-white">{selectedCita ? 'Editar Cita' : 'Nueva Cita'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg"><X size={18} className="text-slate-500" /></button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelClass}>Cliente *</label><div className="relative"><User size={14} className="absolute left-3 top-2.5 text-slate-400" /><input value={form.cliente_nombre} onChange={e => setForm({...form, cliente_nombre: e.target.value})} className={`${inputClass} pl-9`} placeholder="Nombre completo" /></div></div>
                <div><label className={labelClass}>Documento</label><input value={form.cliente_documento} onChange={e => setForm({...form, cliente_documento: e.target.value})} className={inputClass} placeholder="CC / NIT" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelClass}>Teléfono</label><div className="relative"><Phone size={14} className="absolute left-3 top-2.5 text-slate-400" /><input value={form.cliente_telefono} onChange={e => setForm({...form, cliente_telefono: e.target.value})} className={`${inputClass} pl-9`} placeholder="3XX XXX XXXX" /></div></div>
                <div><label className={labelClass}>Email</label><div className="relative"><Mail size={14} className="absolute left-3 top-2.5 text-slate-400" /><input value={form.cliente_email} onChange={e => setForm({...form, cliente_email: e.target.value})} className={`${inputClass} pl-9`} placeholder="correo@ejemplo.com" /></div></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className={labelClass}>Fecha *</label><input type="date" value={form.fecha} onChange={e => setForm({...form, fecha: e.target.value})} className={inputClass} /></div>
                <div><label className={labelClass}>Hora *</label><select value={form.hora} onChange={e => setForm({...form, hora: e.target.value})} className={inputClass}>{HORAS.map(h => <option key={h} value={h}>{h}</option>)}</select></div>
                <div><label className={labelClass}>Tipo</label><select value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})} className={inputClass}>{TIPOS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelClass}>Expediente</label><div className="relative"><FileText size={14} className="absolute left-3 top-2.5 text-slate-400" /><input value={form.expediente} onChange={e => setForm({...form, expediente: e.target.value})} className={`${inputClass} pl-9`} placeholder="Número" /></div></div>
                <div><label className={labelClass}>Estado</label><select value={form.estado} onChange={e => setForm({...form, estado: e.target.value})} className={inputClass}>{ESTADOS.map(e => <option key={e} value={e}>{e.charAt(0).toUpperCase()+e.slice(1)}</option>)}</select></div>
              </div>
              <div><label className={labelClass}>Notas</label><textarea value={form.notas || ''} onChange={e => setForm({...form, notas: e.target.value})} className={`${inputClass} h-20 resize-none`} placeholder="Observaciones..." /></div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 dark:border-gray-800 flex justify-between">
              {selectedCita && (
                <button onClick={() => { handleDelete(selectedCita.id); setShowModal(false); }} className="px-4 py-2 text-red-600 text-xs font-semibold flex items-center gap-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"><Trash2 size={14} /> Eliminar</button>
              )}
              <div className="flex gap-2 ml-auto">
                <button onClick={() => setShowModal(false)} className="px-4 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg">Cancelar</button>
                <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 hover:bg-blue-700 disabled:opacity-50">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {selectedCita ? 'Guardar' : 'Crear'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendario;
